// frontend/src/components/TwoFactorAdminStats.js
// Admin dashboard component for 2FA adoption statistics

import React, { useState, useEffect } from 'react';
import api from '../services/api';

const TwoFactorAdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/2fa/stats');
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch 2FA stats:', err);
      setError('Failed to load 2FA statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="two-factor-admin-stats">
        <div className="loading">Loading 2FA statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="two-factor-admin-stats">
        <div className="error-message">{error}</div>
        <button onClick={fetchStats} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="two-factor-admin-stats">
        <div className="no-data">No 2FA statistics available</div>
      </div>
    );
  }

  return (
    <div className="two-factor-admin-stats">
      <div className="stats-header">
        <h3>Two-Factor Authentication Statistics</h3>
        <button onClick={fetchStats} className="btn btn-outline btn-sm">
          Refresh
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total_users || 0}</div>
          <div className="stat-label">Total Active Users</div>
        </div>

        <div className="stat-card enabled">
          <div className="stat-value">{stats.users_with_2fa || 0}</div>
          <div className="stat-label">Users with 2FA Enabled</div>
        </div>

        <div className="stat-card adoption">
          <div className="stat-value">{stats.adoption_rate || 0}%</div>
          <div className="stat-label">2FA Adoption Rate</div>
        </div>
      </div>

      {stats.recentSetups && stats.recentSetups.length > 0 && (
        <div className="recent-setups">
          <h4>Recent 2FA Setups (Last 30 Days)</h4>
          <div className="setups-chart">
            {stats.recentSetups.map((setup, index) => (
              <div key={index} className="setup-item">
                <div className="setup-date">
                  {new Date(setup.date).toLocaleDateString()}
                </div>
                <div className="setup-count">
                  <div 
                    className="setup-bar" 
                    style={{ 
                      width: `${Math.max((setup.setups / Math.max(...stats.recentSetups.map(s => s.setups))) * 100, 10)}%` 
                    }}
                  >
                    {setup.setups}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="security-recommendations">
        <h4>Security Recommendations</h4>
        <div className="recommendations">
          {stats.adoption_rate < 25 && (
            <div className="recommendation warning">
              <span className="icon">⚠️</span>
              <div>
                <strong>Low 2FA Adoption:</strong> Consider implementing 2FA incentives or requirements for sensitive operations.
              </div>
            </div>
          )}
          
          {stats.adoption_rate >= 25 && stats.adoption_rate < 75 && (
            <div className="recommendation info">
              <span className="icon">💡</span>
              <div>
                <strong>Moderate 2FA Adoption:</strong> Consider education campaigns to increase 2FA usage for better security.
              </div>
            </div>
          )}
          
          {stats.adoption_rate >= 75 && (
            <div className="recommendation success">
              <span className="icon">✅</span>
              <div>
                <strong>Good 2FA Adoption:</strong> Your users are well-protected with strong 2FA adoption rates.
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .two-factor-admin-stats {
          background: #fff;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .stats-header h3 {
          margin: 0;
          color: #333;
        }

        .loading, .error-message, .no-data {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error-message {
          color: #dc3545;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          border: 2px solid transparent;
          transition: all 0.2s;
        }

        .stat-card.enabled {
          border-color: #28a745;
          background: #f8fff9;
        }

        .stat-card.adoption {
          border-color: #007bff;
          background: #f8fbff;
        }

        .stat-value {
          font-size: 32px;
          font-weight: bold;
          color: #333;
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: #666;
          text-transform: uppercase;
          font-weight: 500;
        }

        .recent-setups {
          margin-bottom: 30px;
        }

        .recent-setups h4 {
          margin-bottom: 15px;
          color: #333;
        }

        .setups-chart {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          max-height: 200px;
          overflow-y: auto;
        }

        .setup-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .setup-date {
          width: 100px;
          flex-shrink: 0;
          color: #666;
        }

        .setup-count {
          flex: 1;
          margin-left: 15px;
        }

        .setup-bar {
          background: linear-gradient(90deg, #007bff, #0056b3);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: bold;
          min-width: 30px;
          text-align: center;
          font-size: 12px;
        }

        .security-recommendations h4 {
          margin-bottom: 15px;
          color: #333;
        }

        .recommendations {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .recommendation {
          display: flex;
          align-items: flex-start;
          padding: 12px;
          border-radius: 8px;
          gap: 10px;
        }

        .recommendation.warning {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
        }

        .recommendation.info {
          background: #d1ecf1;
          border: 1px solid #bee5eb;
        }

        .recommendation.success {
          background: #d4edda;
          border: 1px solid #c3e6cb;
        }

        .recommendation .icon {
          font-size: 16px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .recommendation div {
          flex: 1;
        }

        .btn {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
          text-decoration: none;
          display: inline-block;
        }

        .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .btn-outline {
          background: transparent;
          color: #007bff;
          border: 1px solid #007bff;
        }

        .btn-outline:hover:not(:disabled) {
          background: #007bff;
          color: white;
        }

        @media (max-width: 768px) {
          .stats-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .setup-item {
            flex-direction: column;
            align-items: flex-start;
          }

          .setup-count {
            margin-left: 0;
            margin-top: 5px;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default TwoFactorAdminStats;